/**
 * Impersonacion de tenant: permite a un platform_admin operar /admin como si fuera
 * admin de un tenant especifico. Se guarda en sessionStorage para que solo viva
 * en la pestania actual (evita confusiones cross-tabs).
 */
import { useEffect, useState } from "react";
import { useAuth } from "./auth";

const KEY = "fideliza:impersonate-tenant";

export function getImpersonatedTenant(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setImpersonatedTenant(tenantId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, tenantId);
    // Disparamos un evento para que listeners reaccionen (TenantProvider, banner)
    window.dispatchEvent(new Event("impersonation-changed"));
  } catch {
    /* ignore */
  }
}

export function clearImpersonatedTenant(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
    window.dispatchEvent(new Event("impersonation-changed"));
  } catch {
    /* ignore */
  }
}

/**
 * Hook reactivo: devuelve el tenantId actual de impersonacion (o null).
 * Re-renderiza cuando cambia via setImpersonatedTenant.
 */
export function useImpersonatedTenant(): string | null {
  const [tenantId, setTenantId] = useState<string | null>(() => getImpersonatedTenant());
  useEffect(() => {
    const onChange = () => setTenantId(getImpersonatedTenant());
    window.addEventListener("impersonation-changed", onChange);
    return () => window.removeEventListener("impersonation-changed", onChange);
  }, []);
  return tenantId;
}

/**
 * Devuelve el tenantId que el usuario opera de hecho:
 * - Si el user es platform_admin y hay impersonacion activa, devuelve la impersonada
 * - Sino, devuelve el claims.tenantId del usuario
 *
 * Usalo en hooks y queries que dependen del tenant actual.
 */
export function useEffectiveTenantId(): string | undefined {
  const { claims } = useAuth();
  const impersonated = useImpersonatedTenant();
  if (claims.role === "platform_admin" && impersonated) return impersonated;
  return claims.tenantId ?? undefined;
}

